import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plane, Ship, ShieldCheck, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HERO_IMAGE, AIR_IMAGE, SEA_IMAGE } from "@/lib/brand";

const SAMPLES = ["HTL-2026-88491", "HTL-2026-10294", "HTL-2026-55410"];

export default function Home() {
  const [value, setValue] = useState("");
  const [mode, setMode] = useState("tracking");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    const input = value.trim();
    if (!input) return;
    if (mode === "phone") navigate(`/suivi-telephone?phone=${encodeURIComponent(input)}`);
    else navigate(`/suivi/${encodeURIComponent(input.toUpperCase())}`);
  };

  return (
    <div className="bg-[#F8FAFC]">
      <section className="relative overflow-hidden border-b border-slate-200 bg-[#1E3A8A]">
        <img
          src={HERO_IMAGE}
          alt="Fret conteneurs"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A] via-[#1E3A8A]/85 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-blue-200">
            Fret aérien &amp; maritime · Québec → Abidjan
          </p>
          <h1 className="mt-4 max-w-2xl text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Suivez votre colis à chaque étape du trajet
          </h1>
          <p className="mt-4 max-w-xl text-base text-blue-100 sm:text-lg">
            Entrez votre numéro de suivi Haba Trans Logistique pour voir le statut en temps réel.
          </p>

          <form
            onSubmit={submit}
            className="mt-8 max-w-2xl rounded-2xl border border-white/20 bg-white/95 p-4 shadow-2xl backdrop-blur sm:p-5"
            data-testid="tracking-search-form"
          >
            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              {mode === "phone" ? "Numéro de téléphone" : "Numéro de suivi"}
            </label>
            <div className="mt-2 flex gap-2">
              {[
                { key: "tracking", label: "Numéro de suivi" },
                { key: "phone", label: "Téléphone" },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => {
                    setMode(m.key);
                    setValue("");
                  }}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                    mode === m.key
                      ? "bg-[#1E3A8A] text-white"
                      : "border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`search-mode-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <Input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={mode === "phone" ? "+225 07 45 88 21" : "HTL-2026-XXXXX"}
                  className={`h-14 rounded-xl border-slate-300 pl-12 font-mono text-base tracking-wider focus-visible:ring-[#2563EB] ${
                    mode === "phone" ? "" : "uppercase"
                  }`}
                  data-testid="tracking-search-input"
                />
              </div>
              <Button
                type="submit"
                className="h-14 rounded-xl bg-[#2563EB] px-8 text-base font-bold text-white transition-colors hover:bg-[#1D4ED8]"
                data-testid="tracking-search-submit"
              >
                Suivre mon colis
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Exemples :</span>
              {SAMPLES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => navigate(`/suivi/${s}`)}
                  className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-[#1E3A8A] transition-colors hover:bg-blue-100"
                  data-testid={`sample-tracking-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </form>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-20">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Nos services d'expédition</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { title: "Fret aérien", desc: "Livraison rapide en 5 à 10 jours vers Abidjan.", img: AIR_IMAGE, icon: Plane },
            { title: "Fret maritime", desc: "Conteneurs groupés, la solution économique pour les gros volumes.", img: SEA_IMAGE, icon: Ship },
          ].map((s) => (
            <article
              key={s.title}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg"
            >
              <div className="h-40 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <s.icon className="h-5 w-5 text-[#2563EB]" />
                  <h3 className="text-lg font-semibold text-slate-900">{s.title}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-600">{s.desc}</p>
              </div>
            </article>
          ))}
          <article className="rounded-2xl border border-slate-200 bg-[#1E3A8A] p-6 text-white shadow-sm">
            <ShieldCheck className="h-6 w-6 text-blue-200" />
            <h3 className="mt-3 text-lg font-semibold">Suivi sécurisé</h3>
            <p className="mt-2 text-sm text-blue-100">
              Vos données sensibles restent masquées sur la page publique de suivi.
            </p>
            <div className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-blue-200">
              <MapPin className="h-4 w-4" /> Montréal · Québec · Abidjan
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
